import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Plus, Search, Trash2, X, FileText, Briefcase, Mail, Phone, MapPin, Building2, Pencil, Download, FilePlus, StickyNote, CheckSquare, Square, AlertTriangle, CalendarClock, Star } from "lucide-react";
import { API } from "@/lib/api";
import ReviewRequestForCustomer from "@/components/ReviewRequestForCustomer";
import { CustomerRelationshipsPanel } from "@/components/CustomerRelationshipsPanel";
import { ContactCommunicationPanel } from "@/components/ContactCommunicationPanel";
import { EasyCarVIN24CustomerContext } from "@/components/EasyCarVIN24CustomerContext";
import { fmtCurrency, fmtDate } from "@/lib/quote-helpers";
import { docLabel, offerLabel, offerLabelPlural, STATUS_META, CUSTOMER_STATUS_META, CUSTOMER_STATUSES, CRM_STAGE_META, CRM_STAGES, EMAIL_TEMPLATES } from "@/lib/constants";
import { useOrgCurrency } from "@/lib/currency-context";

const emptyForm = {
  kunde_name: "", kunde_firma: "", anrede: "", kunde_email: "", kunde_telefon: "",
  kunde_strasse: "", kunde_plz_ort: "", kunde_land: "Deutschland", kunde_ustid: "",
  kunde_einvoice_endpoint: "", kunde_einvoice_endpoint_scheme: "", kunde_buyer_reference: "", tags: [],
};

export const KundenTab = ({ company, openQuoteById, onNewQuoteForCustomer, onNewInvoiceForCustomer }) => {
  const { currency, formatMoney } = useOrgCurrency();
  const singularOfferLabel = offerLabel(company);
  const pluralOfferLabel = offerLabelPlural(company);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [emailModal, setEmailModal] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [reviewCustomer, setReviewCustomer] = useState(null);
  const [dealModal, setDealModal] = useState(null);
  const [dealSaving, setDealSaving] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team/members`)
      .then((r) => setMembers(r.data?.members || []))
      .catch(() => setMembers([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/customers`);
      setCustomers(r.data || []);
    } catch { setCustomers([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadOverview = async (id) => {
    setSelectedId(id);
    setOvLoading(true);
    setOverview(null);
    try {
      const r = await axios.get(`${API}/customers/${id}/overview`);
      setOverview(r.data);
    } catch { toast.error("Kundendaten konnten nicht geladen werden"); }
    finally { setOvLoading(false); }
  };

  const openNew = () => setForm({ ...emptyForm });
  const openEdit = (c) => setForm({ ...emptyForm, ...c });

  const save = async () => {
    if (!form.kunde_name.trim() && !form.kunde_firma.trim()) {
      toast.error("Bitte Name oder Firma angeben"); return;
    }
    setSaving(true);
    try {
      if (form.id) await axios.put(`${API}/customers/${form.id}`, form);
      else await axios.post(`${API}/customers`, form);
      toast.success("Kunde gespeichert");
      setForm(null);
      await load();
      if (form.id && selectedId === form.id) loadOverview(form.id);
    } catch (e) {
      if (!form.id && e.response?.status === 409) {
        const msg = e.response?.data?.detail || "Mögliches Duplikat gefunden.";
        if (window.confirm(`⚠️ ${msg}\n\nTrotzdem als neuen Kunden anlegen?`)) {
          try {
            await axios.post(`${API}/customers?force=true`, form);
            toast.success("Kunde gespeichert");
            setForm(null);
            await load();
          } catch { toast.error("Speichern fehlgeschlagen"); }
        }
      } else {
        toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
      }
    }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    const label = c.kunde_firma || c.kunde_name || "diesen Kontakt";
    if (!window.confirm(`Diesen Kontakt wirklich löschen?\n\n„${label}“ wird zunächst in den CRM-Papierkorb verschoben. Angebote, Rechnungen und Dokumente bleiben erhalten.`)) return;
    try {
      await axios.post(`${API}/crm/recycle-bin/customers/${c.id}`);
      toast.success("Kontakt in den Papierkorb verschoben");
      if (selectedId === c.id) { setSelectedId(null); setOverview(null); }
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Verschieben in den Papierkorb fehlgeschlagen"); }
  };

  const addNote = async () => {
    if (!note.trim() || !selectedId) return;
    try {
      const r = await axios.post(`${API}/customers/${selectedId}/note`, { text: note });
      setOverview((o) => ({ ...o, notes: [...(o.notes || []), r.data] }));
      setNote("");
    } catch { toast.error("Notiz konnte nicht gespeichert werden"); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/customers/${selectedId}/note/${noteId}`);
      setOverview((o) => ({ ...o, notes: (o.notes || []).filter((n) => n.id !== noteId) }));
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/customers/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "kunden-export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Kundenliste exportiert");
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API}/customers/${id}/status`, { status });
      setCustomers((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
      setOverview((o) => (o && o.customer.id === id ? { ...o, status, customer: { ...o.customer, status } } : o));
    } catch { toast.error("Status konnte nicht geändert werden"); }
  };

  const addTask = async () => {
    if (!taskTitle.trim() || !taskDue || !selectedId) { toast.error("Titel und Fälligkeitsdatum angeben"); return; }
    try {
      const r = await axios.post(`${API}/customers/${selectedId}/task`, { title: taskTitle, due: taskDue });
      setOverview((o) => ({ ...o, tasks: [...(o.tasks || []), r.data] }));
      setTaskTitle(""); setTaskDue("");
      toast.success("Aufgabe erstellt – erscheint auch im Kalender");
    } catch (e) { toast.error(e.response?.data?.detail || "Aufgabe konnte nicht erstellt werden"); }
  };

  const toggleTask = async (taskId) => {
    try {
      const r = await axios.post(`${API}/customers/${selectedId}/task/${taskId}/toggle`);
      setOverview((o) => ({ ...o, tasks: (o.tasks || []).map((t) => (t.id === taskId ? { ...t, done: r.data.done } : t)) }));
    } catch { toast.error("Fehlgeschlagen"); }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/customers/${selectedId}/task/${taskId}`);
      setOverview((o) => ({ ...o, tasks: (o.tasks || []).filter((t) => t.id !== taskId) }));
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const sendEmail = async () => {
    if (!emailModal?.subject?.trim() || !emailModal?.body?.trim()) { toast.error("Betreff und Nachricht angeben"); return; }
    setEmailSending(true);
    try {
      const r = await axios.post(`${API}/customers/${selectedId}/email`, { subject: emailModal.subject, body: emailModal.body });
      toast.success("E-Mail gesendet");
      if (r.data?.note) setOverview((o) => ({ ...o, notes: [...(o.notes || []), r.data.note] }));
      setEmailModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || "E-Mail konnte nicht gesendet werden"); }
    finally { setEmailSending(false); }
  };

  const applyTemplate = (tpl) => {
    const c = overview?.customer || {};
    const repl = (s) => (s || "")
      .replace(/\{name\}/g, c.kunde_name || "")
      .replace(/\{firma\}/g, c.kunde_firma || "");
    setEmailModal((m) => ({ ...m, subject: repl(tpl.subject), body: repl(tpl.body) }));
  };

  const prepareReviewEmail = (payload) => {
    setReviewCustomer(null);
    setEmailModal(payload);
  };

  const addReviewNote = async (text) => {
    if (!selectedId || !text) return;
    const r = await axios.post(`${API}/customers/${selectedId}/note`, { text });
    setOverview((o) => ({ ...o, notes: [...(o.notes || []), r.data] }));
  };

  const createDeal = async () => {
    if (!dealModal?.title?.trim()) { toast.error("Bitte einen Titel angeben"); return; }
    setDealSaving(true);
    try {
      const c = overview.customer;
      await axios.post(`${API}/crm/deals`, {
        title: dealModal.title,
        customer_name: c.kunde_firma || c.kunde_name || "",
        value: Number(dealModal.value) || 0,
        stage: dealModal.stage || "neu",
      });
      toast.success("Deal angelegt");
      setDealModal(null);
      loadOverview(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Deal konnte nicht angelegt werden"); }
    finally { setDealSaving(false); }
  };

  const filtered = customers.filter((c) => {
    if (statusFilter !== "alle" && (c.status || "lead") !== statusFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [c.kunde_firma, c.firma, c.firmenname, c.company, c.kunde_name, c.name, c.kunde_email].some((v) => (v || "").toLowerCase().includes(s));
  });

  // Die Übersicht verwendet ausschließlich vorhandene Daten und verändert keine Detailfelder.
  // Firmenname hat Vorrang; „Unbenannt“ erscheint nur bei einem tatsächlich leeren Datensatz.
  const label = (c = {}) => [
    c.kunde_firma, c.firma, c.firmenname, c.company, c.kunde_name, c.name,
  ].map((value) => String(value || "").trim()).find(Boolean) || "Unbenannt";

  return (
    <div data-testid="kunden-view" className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
      {/* Liste */}
      <Card className="p-4 border-[var(--brand-border)] h-fit">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[var(--brand-primary)]" />
            <span className="font-semibold text-[var(--brand-ink)]">Kunden ({customers.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={exportCsv} className="border-[var(--brand-border)]" data-testid="kunden-export-btn" title="Als CSV exportieren">
              <Download className="w-4 h-4" />
            </Button>
            <Button size="sm" onClick={openNew} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="kunden-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Neu
            </Button>
          </div>
        </div>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="pl-8" data-testid="kunden-search" />
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="kunden-status-filter">
          {["alle", ...CUSTOMER_STATUSES].map((s) => {
            const active = statusFilter === s;
            const meta = CUSTOMER_STATUS_META[s];
            return (
              <button key={s} type="button" onClick={() => setStatusFilter(s)}
                data-testid={`kunden-filter-${s}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${active ? "text-white border-transparent" : "text-[var(--brand-muted)] border-[var(--brand-border)] hover:border-[var(--brand-primary)]"}`}
                style={active ? { background: meta ? meta.color : "var(--brand-primary)" } : {}}>
                {meta ? meta.label : "Alle"}
              </button>
            );
          })}
        </div>
        {loading ? (
          <p className="text-sm text-[var(--brand-muted)]">Lädt …</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[var(--brand-muted)]" data-testid="kunden-empty">Keine Kunden gefunden. Kunden entstehen automatisch aus Angeboten/Rechnungen – oder lege manuell einen an.</p>
        ) : (
          <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
            {filtered.map((c) => {
              const sm = CUSTOMER_STATUS_META[c.status || "lead"] || CUSTOMER_STATUS_META.lead;
              return (
              <button key={c.id} onClick={() => loadOverview(c.id)}
                className={`w-full text-left rounded-lg border p-2.5 transition ${selectedId === c.id ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]" : "border-[var(--brand-border-light)] hover:border-[var(--brand-primary)]"}`}
                data-testid={`kunden-item-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{label(c)}</div>
                  <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ background: sm.color }} data-testid={`kunden-item-status-${c.id}`}>{sm.label}</span>
                </div>
                <div className="text-xs text-[var(--brand-muted)] truncate">{c.kunde_firma && c.kunde_name ? c.kunde_name : (c.kunde_email || c.kunde_plz_ort || "—")}</div>
              </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Detail */}
      <div>
        {!selectedId ? (
          <Card className="p-10 border-[var(--brand-border)] text-center" data-testid="kunden-detail-empty">
            <Users className="w-10 h-10 mx-auto text-[var(--brand-border)] mb-3" />
            <p className="text-[var(--brand-muted)]">Wähle links einen Kunden, um Kontaktdaten, Umsatz und die komplette Dokument-Historie zu sehen.</p>
          </Card>
        ) : ovLoading ? (
          <Card className="p-8 border-[var(--brand-border)]"><p className="text-sm text-[var(--brand-muted)]">Lädt …</p></Card>
        ) : overview ? (
          <div className="space-y-5" data-testid="kunden-detail">
            {/* Kopf + Kontakt */}
            <Card className="p-5 border-[var(--brand-border)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-[Playfair_Display] text-2xl font-semibold text-[var(--brand-ink)]">{label(overview.customer)}</h2>
                  {overview.customer.kunde_firma && overview.customer.kunde_name && (
                    <p className="text-sm text-[var(--brand-muted)]">{overview.customer.anrede ? overview.customer.anrede + " " : ""}{overview.customer.kunde_name}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2" data-testid="kunden-status-select-wrap">
                    <span className="text-xs text-[var(--brand-muted)]">Status:</span>
                    {CUSTOMER_STATUSES.map((s) => {
                      const meta = CUSTOMER_STATUS_META[s];
                      const active = (overview.customer.status || "lead") === s;
                      return (
                        <button key={s} type="button" onClick={() => changeStatus(overview.customer.id, s)}
                          data-testid={`kunden-set-status-${s}`}
                          className={`text-[11px] px-2 py-0.5 rounded-full border transition ${active ? "text-white border-transparent" : "text-[var(--brand-muted)] border-[var(--brand-border)] hover:border-[var(--brand-primary)]"}`}
                          style={active ? { background: meta.color } : {}}>
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button size="sm" onClick={() => onNewQuoteForCustomer && onNewQuoteForCustomer(overview.customer)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="kunden-new-quote-btn"><FilePlus className="w-4 h-4 mr-1" /> Neues Angebot</Button>
                  <Button size="sm" variant="outline" onClick={() => onNewInvoiceForCustomer && onNewInvoiceForCustomer(overview.customer)} title="Neue Rechnung mit den Daten dieses Kunden starten" className="border-[var(--brand-border)]" data-testid="kunden-new-invoice-btn"><FileText className="w-4 h-4 mr-1" /> Rechnung</Button>
                  <Button size="sm" variant="outline" onClick={() => setEmailModal({ subject: "", body: "" })} disabled={!overview.customer.kunde_email} title={overview.customer.kunde_email ? "E-Mail senden" : "Keine E-Mail-Adresse hinterlegt"} className="border-[var(--brand-border)]" data-testid="kunden-email-btn"><Mail className="w-4 h-4 mr-1" /> E-Mail</Button>
                  <Button size="sm" variant="outline" onClick={() => setReviewCustomer(overview.customer)} title="Google-Bewertung für diesen Kunden vorbereiten" className="border-[var(--brand-border)]" data-testid="kunden-review-request-btn"><Star className="w-4 h-4 mr-1" /> Bewertung</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(overview.customer)} className="border-[var(--brand-border)]" data-testid="kunden-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Bearbeiten</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(overview.customer)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="kunden-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Löschen</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4 text-sm">
                {overview.customer.kunde_email && <div className="flex items-center gap-2 text-[var(--brand-ink)]"><Mail className="w-4 h-4 text-[var(--brand-muted)]" /> {overview.customer.kunde_email}</div>}
                {overview.customer.kunde_telefon && <div className="flex items-center gap-2 text-[var(--brand-ink)]"><Phone className="w-4 h-4 text-[var(--brand-muted)]" /> {overview.customer.kunde_telefon}</div>}
                {(overview.customer.kunde_strasse || overview.customer.kunde_plz_ort) && <div className="flex items-center gap-2 text-[var(--brand-ink)]"><MapPin className="w-4 h-4 text-[var(--brand-muted)]" /> {[overview.customer.kunde_strasse, overview.customer.kunde_plz_ort].filter(Boolean).join(", ")}</div>}
                {overview.customer.kunde_ustid && <div className="flex items-center gap-2 text-[var(--brand-ink)]"><Building2 className="w-4 h-4 text-[var(--brand-muted)]" /> USt-IdNr.: {overview.customer.kunde_ustid}</div>}
              </div>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kunden-kpis">
              <Card className="p-4 border-[var(--brand-border)]">
                <div className="text-xs text-[var(--brand-muted)]">Gesamt fakturiert</div>
                <div className="text-xl font-bold text-[var(--brand-ink)]">{formatMoney(overview.stats.total_invoiced)}</div>
              </Card>
              <Card className="p-4 border-[var(--brand-border)]">
                <div className="text-xs text-[var(--brand-muted)]">Offener Betrag</div>
                <div className={`text-xl font-bold ${overview.stats.open_amount > 0 ? "text-amber-600" : "text-[var(--brand-ink)]"}`}>{formatMoney(overview.stats.open_amount)}</div>
              </Card>
              <Card className={`p-4 border-[var(--brand-border)] ${overview.stats.overdue_amount > 0 ? "bg-red-50 border-red-200" : ""}`} data-testid="kunden-kpi-overdue">
                <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1">{overview.stats.overdue_amount > 0 && <AlertTriangle className="w-3 h-3 text-red-500" />} Überfällig</div>
                <div className={`text-xl font-bold ${overview.stats.overdue_amount > 0 ? "text-red-600" : "text-[var(--brand-ink)]"}`}>{formatMoney(overview.stats.overdue_amount)}</div>
                <div className="text-[11px] text-[var(--brand-muted)]">{overview.stats.overdue_count} überfällige Rechn.</div>
              </Card>
              <Card className="p-4 border-[var(--brand-border)]">
                <div className="text-xs text-[var(--brand-muted)]">Dokumente</div>
                <div className="text-xl font-bold text-[var(--brand-ink)]">{overview.stats.invoice_count + overview.stats.quote_count}</div>
                <div className="text-[11px] text-[var(--brand-muted)]">{overview.stats.invoice_count} Rechn. · {overview.stats.quote_count} Angeb.</div>
              </Card>
              <Card className="p-4 border-[var(--brand-border)]">
                <div className="text-xs text-[var(--brand-muted)]">Offene Aufgaben</div>
                <div className={`text-xl font-bold ${overview.stats.open_tasks > 0 ? "text-[var(--brand-primary)]" : "text-[var(--brand-ink)]"}`}>{overview.stats.open_tasks || 0}</div>
                <div className="text-[11px] text-[var(--brand-muted)]">{overview.stats.deal_count} Deals · {formatMoney(overview.stats.deal_value)}</div>
              </Card>
            </div>

            <CustomerRelationshipsPanel customerId={overview.customer.id} />
            <EasyCarVIN24CustomerContext customerId={overview.customer.id} />
            <ContactCommunicationPanel kind="customer" contact={{ id: overview.customer.id, name: overview.customer.kunde_name, firma: overview.customer.kunde_firma, email: overview.customer.kunde_email, mobil: overview.customer.kunde_mobil, telefon: overview.customer.kunde_telefon }} />

            {/* Notizen / Kontakthistorie */}
            <Card className="p-5 border-[var(--brand-border)]">
              <div className="flex items-center gap-2 mb-3">
                <StickyNote className="w-4 h-4 text-[var(--brand-primary)]" />
                <span className="font-semibold text-[var(--brand-ink)]">Notizen & Kontakthistorie</span>
              </div>
              <div className="flex gap-2 mb-3">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz erfassen (z. B. Telefonat, Absprache) …"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }} data-testid="kunden-note-input" />
                <Button onClick={addNote} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white shrink-0" data-testid="kunden-note-add">Hinzufügen</Button>
              </div>
              {(overview.notes || []).length === 0 ? (
                <p className="text-sm text-[var(--brand-muted)]">Noch keine Notizen. Halte hier Telefonate, E-Mails und Absprachen fest.</p>
              ) : (
                <div className="space-y-2" data-testid="kunden-notes">
                  {[...(overview.notes || [])].reverse().map((n) => (
                    <div key={n.id} className="group flex items-start justify-between gap-3 border-l-2 border-[var(--brand-primary)] pl-3 py-1">
                      <div className="min-w-0">
                        <div className="text-sm text-[var(--brand-ink)] whitespace-pre-wrap break-words">{n.text}</div>
                        <div className="text-[11px] text-[var(--brand-muted)]">{n.at ? new Date(n.at).toLocaleString("de-DE") : ""}</div>
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="opacity-0 group-hover:opacity-100 text-[var(--brand-muted)] hover:text-red-600 transition shrink-0" data-testid={`kunden-note-del-${n.id}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Aufgaben / Follow-ups */}
            <Card className="p-5 border-[var(--brand-border)]" data-testid="kunden-tasks-card">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-4 h-4 text-[var(--brand-primary)]" />
                <span className="font-semibold text-[var(--brand-ink)]">Aufgaben & Follow-ups</span>
                <span className="text-xs text-[var(--brand-muted)]">– erscheinen automatisch im Kalender</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="z. B. Angebot nachfassen …"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }} data-testid="kunden-task-title" />
                <Input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="sm:w-44" data-testid="kunden-task-due" />
                <Button onClick={addTask} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white shrink-0" data-testid="kunden-task-add"><Plus className="w-4 h-4 mr-1" /> Aufgabe</Button>
              </div>
              {(overview.tasks || []).length === 0 ? (
                <p className="text-sm text-[var(--brand-muted)]">Noch keine Aufgaben. Lege Wiedervorlagen an, um keinen Follow-up zu verpassen.</p>
              ) : (
                <div className="space-y-1.5" data-testid="kunden-tasks">
                  {[...(overview.tasks || [])].sort((a, b) => (a.start || "").localeCompare(b.start || "")).map((t) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const overdue = !t.done && t.start && t.start < today;
                    return (
                      <div key={t.id} className={`group flex items-center gap-3 rounded-lg border px-3 py-2 transition ${overdue ? "border-red-200 bg-red-50" : "border-[var(--brand-border-light)]"}`} data-testid={`kunden-task-${t.id}`}>
                        <button onClick={() => toggleTask(t.id)} className="shrink-0 text-[var(--brand-primary)]" data-testid={`kunden-task-toggle-${t.id}`}>
                          {t.done ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-[var(--brand-muted)]" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-medium truncate ${t.done ? "line-through text-[var(--brand-muted)]" : "text-[var(--brand-ink)]"}`}>{t.title}</div>
                          <div className={`text-[11px] flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : "text-[var(--brand-muted)]"}`}>
                            {overdue && <AlertTriangle className="w-3 h-3" />} fällig {fmtDate(t.start)}
                          </div>
                        </div>
                        <button onClick={() => deleteTask(t.id)} className="opacity-0 group-hover:opacity-100 text-[var(--brand-muted)] hover:text-red-600 transition shrink-0" data-testid={`kunden-task-del-${t.id}`}><X className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Dokument-Historie */}
            <Card className="p-5 border-[var(--brand-border)]">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-[var(--brand-primary)]" />
                <span className="font-semibold text-[var(--brand-ink)]">Dokument-Historie</span>
              </div>
              {overview.documents.length === 0 ? (
                <p className="text-sm text-[var(--brand-muted)]">Noch keine Dokumente für diesen Kunden.</p>
              ) : (
                <div className="space-y-1.5" data-testid="kunden-documents">
                  {overview.documents.map((d) => {
                    const meta = STATUS_META[d.status] || { label: d.status, color: "#9CA3AF" };
                    return (
                      <button key={d.id} onClick={() => openQuoteById && openQuoteById(d.id)}
                        className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition text-left ${d.overdue ? "border-red-200 bg-red-50 hover:border-red-400" : "border-[var(--brand-border-light)] hover:border-[var(--brand-primary)]"}`}
                        data-testid={`kunden-doc-${d.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{d.angebot_nr} · {docLabel(d.dokument_typ)}</div>
                          <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1">
                            {fmtDate(d.datum)}
                            {d.overdue && <span className="text-red-600 font-medium flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> überfällig seit {fmtDate(d.faellig)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[11px] px-2 py-0.5 rounded-full text-white" style={{ background: meta.color }}>{meta.label}</span>
                          <span className="text-sm font-semibold text-[var(--brand-ink)] w-24 text-right">{formatMoney(d.amount, d.currency || currency)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Deals */}
            <Card className="p-5 border-[var(--brand-border)]" data-testid="kunden-deals-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-[var(--brand-primary)]" />
                  <span className="font-semibold text-[var(--brand-ink)]">Verknüpfte CRM-Deals</span>
                </div>
                <Button size="sm" onClick={() => setDealModal({ title: "", value: "", stage: "neu" })} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="kunden-new-deal-btn"><Plus className="w-4 h-4 mr-1" /> Neuer Deal</Button>
              </div>
              {(overview.deals || []).length === 0 ? (
                <p className="text-sm text-[var(--brand-muted)]" data-testid="kunden-deals-empty">Noch keine Deals. Starte eine Verkaufschance direkt aus diesem Kundenprofil.</p>
              ) : (
                <div className="space-y-1.5" data-testid="kunden-deals">
                  {overview.deals.map((dl) => {
                    const sm = CRM_STAGE_META[dl.stage] || { label: dl.stage, color: "#9CA3AF" };
                    const offerValue = Number(dl.offer_value_net);
                    const value = Number.isFinite(offerValue) ? offerValue : (Number(dl.value) || 0);
                    const linkedOffers = [...(dl.linked_offers || [])].reverse();
                    return (
                      <div key={dl.id} className="rounded-lg border border-[var(--brand-border-light)] px-3 py-2" data-testid={`kunden-deal-${dl.id}`}>
                        <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{dl.title}</div>
                          <span className="text-[11px] px-2 py-0.5 rounded-full text-white inline-block mt-0.5" style={{ background: sm.color }}>{sm.label}</span>
                        </div>
                        <div className="shrink-0 text-right"><span className="text-sm font-semibold text-[var(--brand-ink)]">{formatMoney(value, dl.offer_value_currency || currency)}</span>{Number.isFinite(offerValue) && <div className="text-[10px] text-[var(--brand-muted)]">neueste {singularOfferLabel} · netto</div>}</div>
                        </div>
                        {(dl.linked_offers || []).length > 0 && <div className="mt-2 border-t border-[var(--brand-border-light)] pt-2 space-y-1" data-testid={`kunden-deal-offers-${dl.id}`}>
                          <div className="text-[11px] font-medium text-[var(--brand-muted)]">{linkedOffers.length} verknüpfte {pluralOfferLabel} · Varianten werden nicht addiert</div>
                          {linkedOffers.map((offer, index) => <div key={offer.id} className="flex items-center justify-between gap-3 rounded border border-[var(--brand-border-light)] bg-[var(--brand-bg)] px-2 py-1.5 text-xs"><span className="min-w-0 truncate text-[var(--brand-ink)]">{singularOfferLabel} {index + 1} · {offer.angebot_nr || "—"} · {offer.status}</span><span className="shrink-0 text-[var(--brand-muted)]">{formatMoney(offer.net, offer.currency || currency)} netto · Total {formatMoney(offer.gross, offer.currency || currency)}</span></div>)}
                        </div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        ) : null}
      </div>

      {reviewCustomer && <ReviewRequestForCustomer
        customer={reviewCustomer}
        onClose={() => setReviewCustomer(null)}
        onPrepareEmail={prepareReviewEmail}
        onAddNote={addReviewNote}
      />}

      {/* Add/Edit Modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForm(null)}>
          <Card className="w-full max-w-lg bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kunden-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--brand-ink)]">{form.id ? "Kunde bearbeiten" : "Neuer Kunde"}</h3>
              <button onClick={() => setForm(null)} className="p-1" data-testid="kunden-modal-close"><X className="w-5 h-5 text-[var(--brand-muted)]" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Firma</Label><Input value={form.kunde_firma} onChange={(e) => setForm({ ...form, kunde_firma: e.target.value })} data-testid="kunden-firma" /></div>
                <div><Label>Ansprechpartner</Label><Input value={form.kunde_name} onChange={(e) => setForm({ ...form, kunde_name: e.target.value })} data-testid="kunden-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>E-Mail</Label><Input type="email" value={form.kunde_email} onChange={(e) => setForm({ ...form, kunde_email: e.target.value })} data-testid="kunden-email" /></div>
                <div><Label>Telefon</Label><Input value={form.kunde_telefon} onChange={(e) => setForm({ ...form, kunde_telefon: e.target.value })} data-testid="kunden-telefon" /></div>
              </div>
              <div><Label>Straße & Hausnr.</Label><Input value={form.kunde_strasse} onChange={(e) => setForm({ ...form, kunde_strasse: e.target.value })} data-testid="kunden-strasse" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>PLZ & Ort</Label><Input value={form.kunde_plz_ort} onChange={(e) => setForm({ ...form, kunde_plz_ort: e.target.value })} data-testid="kunden-plzort" /></div>
                <div><Label>Land</Label><Input value={form.kunde_land} onChange={(e) => setForm({ ...form, kunde_land: e.target.value })} data-testid="kunden-land" /></div>
              </div>
              <div><Label>USt-IdNr. (optional)</Label><Input value={form.kunde_ustid} onChange={(e) => setForm({ ...form, kunde_ustid: e.target.value })} data-testid="kunden-ustid" /></div>
              <div className="rounded-md border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3 space-y-3">
                <div><p className="text-sm font-medium text-[var(--brand-ink)]">XRechnung (optional)</p><p className="mt-1 text-xs text-[var(--brand-muted)]">Nur erforderlich, wenn dieser Kunde eine XRechnung verlangt. Die Kontakt-E-Mail ist keine elektronische Rechnungsadresse.</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2"><Label>Elektronische Adresse / Leitweg-ID</Label><Input value={form.kunde_einvoice_endpoint || ""} onChange={(e) => setForm({ ...form, kunde_einvoice_endpoint: e.target.value })} placeholder="z. B. 04011000-12345-67" data-testid="kunden-einvoice-endpoint" /></div>
                  <div><Label>CEF-EAS-Code</Label><Input value={form.kunde_einvoice_endpoint_scheme || ""} onChange={(e) => setForm({ ...form, kunde_einvoice_endpoint_scheme: e.target.value })} placeholder="0204" inputMode="numeric" data-testid="kunden-einvoice-scheme" /></div>
                </div>
                <div><Label>Käuferreferenz</Label><Input value={form.kunde_buyer_reference || ""} onChange={(e) => setForm({ ...form, kunde_buyer_reference: e.target.value })} placeholder="z. B. Leitweg-ID oder Bestellreferenz" data-testid="kunden-buyer-reference" /></div>
              </div>
              <div>
                <Label>Tags (kommagetrennt, für Newsletter-Segmente)</Label>
                <Input
                  value={Array.isArray(form.tags) ? form.tags.join(", ") : (form.tags || "")}
                  onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                  placeholder="z. B. stammkunde, newsletter"
                  data-testid="kunden-tags"
                />
              </div>
              {members.length > 1 && (
                <div>
                  <Label>Verantwortlich (Vertrieb)</Label>
                  <select value={form.owner_email || ""} onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                    className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="kunden-owner">
                    <option value="">— Nicht zugeordnet —</option>
                    {members.map((m) => <option key={m.id} value={m.email}>{m.name || m.email}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end mt-5">
              <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="kunden-save">{saving ? "Speichert …" : "Speichern"}</Button>
            </div>
          </Card>
        </div>
      )}

      {/* E-Mail Modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !emailSending && setEmailModal(null)}>
          <Card className="w-full max-w-lg bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kunden-email-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Mail className="w-5 h-5 text-[var(--brand-primary)]" /> E-Mail an Kunden</h3>
              <button onClick={() => setEmailModal(null)} className="p-1" data-testid="kunden-email-close"><X className="w-5 h-5 text-[var(--brand-muted)]" /></button>
            </div>
            <p className="text-xs text-[var(--brand-muted)] mb-3">An: <span className="font-medium text-[var(--brand-ink)]">{overview?.customer?.kunde_email}</span></p>
            <div className="mb-3">
              <Label className="text-xs text-[var(--brand-muted)]">Vorlage</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="kunden-email-templates">
                {EMAIL_TEMPLATES.map((tpl) => (
                  <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-[var(--brand-border)] text-[var(--brand-ink)] hover:border-[var(--brand-primary)] hover:bg-[var(--brand-bg)] transition"
                    data-testid={`kunden-email-tpl-${tpl.id}`}>
                    {tpl.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div><Label>Betreff</Label><Input value={emailModal.subject} onChange={(e) => setEmailModal({ ...emailModal, subject: e.target.value })} placeholder="z. B. Ihr Angebot" data-testid="kunden-email-subject" /></div>
              <div><Label>Nachricht</Label><textarea rows={7} value={emailModal.body} onChange={(e) => setEmailModal({ ...emailModal, body: e.target.value })} placeholder="Ihre Nachricht … (Anrede & Signatur werden automatisch ergänzt)" className="w-full border border-[var(--brand-border)] rounded px-3 py-2 text-sm resize-y" data-testid="kunden-email-body" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setEmailModal(null)} className="border-[var(--brand-border)]" data-testid="kunden-email-cancel">Abbrechen</Button>
              <Button onClick={sendEmail} disabled={emailSending} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="kunden-email-send">{emailSending ? "Sendet …" : "Senden"}</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Neuer Deal Modal */}
      {dealModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !dealSaving && setDealModal(null)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="kunden-deal-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Briefcase className="w-5 h-5 text-[var(--brand-primary)]" /> Neuer Deal</h3>
              <button onClick={() => setDealModal(null)} className="p-1" data-testid="kunden-deal-close"><X className="w-5 h-5 text-[var(--brand-muted)]" /></button>
            </div>
            <p className="text-xs text-[var(--brand-muted)] mb-3">Für: <span className="font-medium text-[var(--brand-ink)]">{overview?.customer?.kunde_firma || overview?.customer?.kunde_name}</span></p>
            <div className="space-y-3">
              <div><Label>Titel</Label><Input value={dealModal.title} onChange={(e) => setDealModal({ ...dealModal, title: e.target.value })} placeholder="z. B. Social-Media-Betreuung 2026" data-testid="kunden-deal-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Wert ({currency})</Label><Input type="number" min="0" step="0.01" value={dealModal.value} onChange={(e) => setDealModal({ ...dealModal, value: e.target.value })} placeholder="z. B. 5000" data-testid="kunden-deal-value" /></div>
                <div>
                  <Label>Phase</Label>
                  <select value={dealModal.stage} onChange={(e) => setDealModal({ ...dealModal, stage: e.target.value })} className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white" data-testid="kunden-deal-stage">
                    {CRM_STAGES.map((s) => <option key={s} value={s}>{CRM_STAGE_META[s].label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setDealModal(null)} className="border-[var(--brand-border)]" data-testid="kunden-deal-cancel">Abbrechen</Button>
              <Button onClick={createDeal} disabled={dealSaving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="kunden-deal-save">{dealSaving ? "Speichert …" : "Deal anlegen"}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
